import React, { useState, useEffect } from 'react';
import { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Select, Paper, Grid, Tabs, Tab, Box, Typography, TextField, MenuItem, Button, List, ListItem, ListItemText, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Chip, Card, CardContent, IconButton, FormControl, InputLabel } from '@mui/material';
import API_BASE_URL from '../../config';
import { CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer 
  } from 'recharts';
import { BarChart, Bar, XAxis as BarXAxis, YAxis as BarYAxis, CartesianGrid as BarCartesianGrid, Tooltip as BarTooltip, Legend as BarLegend } from 'recharts';
import DatePicker from '@mui/lab/DatePicker';
import { TooltipProps } from 'recharts';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import imageCompression from 'browser-image-compression';
import './AdminDashboard.css';

const DeviceDetails = () => {
    const { deviceId } = useParams();
    const [deviceDetails, setDeviceDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedTab, setSelectedTab] = useState('info');
    const [tabData, setTabData] = useState(null);
    const [originalTabData, setOriginalTabData] = useState(null);
    const [isChanged, setIsChanged] = useState(false);
    const [selectedInterval, setSelectedInterval] = useState('24h');
    const [isChartLoading, setIsChartLoading] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [deviceTime, setDeviceTime] = useState(null);
    const [screenStatus, setScreenStatus] = useState(null);
    const [wigWagStatus, setWigWagStatus] = useState(false);
    const [isScreenLoading, setScreenLoading] = useState(false);
    const [systemVersions, setSystemVersions] = useState(null);
    const [isVersionLoading, setVersionLoading] = useState(false);
    const [userDiskFiles, setUserDiskFiles] = useState([]);
    const [systemDiskFiles, setSystemDiskFiles] = useState([]);
    const [isUserDiskLoading, setUserDiskLoading] = useState(false);
    const [isSystemDiskLoading, setSystemDiskLoading] = useState(false);

    const [logChartLoading, setLogChartLoading] = useState(false);
    const [logTableLoading, setLogTableLoading] = useState(false);
    const [logInterval, setLogInterval] = useState('1d');
    const [logDateRange, setLogDateRange] = useState({ from: null, to: null });
    const [trafficStats, setTrafficStats] = useState(null);
    const [logEntries, setLogEntries] = useState([]);
    const [trafficChartData, setTrafficChartData] = useState([]);
    const [timeRange, setTimeRange] = useState('daily');
    const [logDateFilters, setLogDateFilters] = useState({
        from: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            day: new Date().getDate() - 6
        },
        to: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            day: new Date().getDate()
        }
    });
    const [logLimit, setLogLimit] = useState(1000);
    const [freeSpace, setFreeSpace] = useState('--');
    const [selectedSystemFile, setSelectedSystemFile] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [fileNameToDelete, setFileNameToDelete] = useState('');
    const [diskToDelete, setDiskToDelete] = useState('');

    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedUserFile, setSelectedUserFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [writeError, setWriteError] = useState(null);
    const [writeSuccess, setWriteSuccess] = useState(false);
    const fileInputRef = useRef();
    const userFileInputRef = useRef();

    const [forceLogError, setForceLogError] = useState(null);
    const [forceLogSuccess, setForceLogSuccess] = useState(false);

    const [isUpgrading, setIsUpgrading] = useState(false);
    const [upgradeType, setUpgradeType] = useState('');
    const upgradeFileInputRef = useRef();

    const [serviceNotes, setServiceNotes] = useState([]);
    const [serviceNotesLoading, setServiceNotesLoading] = useState(false);
    const [editingLogId, setEditingLogId] = useState(null);
    const [editedLogData, setEditedLogData] = useState({});
    const [logToDelete, setLogToDelete] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [enlargedImage, setEnlargedImage] = useState(null);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);

    const [imageScale, setImageScale] = useState(1);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageRotation, setImageRotation] = useState(0);


    const navigate = useNavigate();
    

    useEffect(() => {
        const fetchDeviceDetails = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/admin/device-basics/${deviceId}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                if (response.status === 401) {
                    navigate('/unauthorized');
                    return;
                }
                const data = await response.json();
                setDeviceDetails(data);
                handleTabChange(null, 'info');
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDeviceDetails();
    }, [deviceId]);

    const fetchChart = async () => {
        setLogChartLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/traffic/chart-data?timeRange=${timeRange}&device=${deviceId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.status === 401) {
                navigate('/unauthorized');
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            const processedData = data.map((entry, index) => {
                const baseDate = new Date();
                // Calculate proper date offsets for each range
                const offsets = {
                    daily: 0,
                    weekly: 6 - index,
                    monthly: 29 - index
                };
                
                baseDate.setDate(baseDate.getDate() - (offsets[timeRange] || 0));
                
                return {
                    time: timeRange === 'daily' 
                        ? `${index}:00` 
                        : baseDate.toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short'
                        }),
                    total: entry.total,
                    speeding: entry.speeding
                };
            });

            const total = processedData.reduce((sum, entry) => sum + entry.total, 0);
            const speeding = processedData.reduce((sum, entry) => sum + entry.speeding, 0);
            setTrafficStats({ total, speeding });
            
            setTrafficChartData(processedData);
            setError(null);

        } catch (error) {
            console.error('Error fetching traffic summaries:', error);
            setError('Failed to fetch traffic summaries. Please try again later.');
        } finally {
            setLogChartLoading(false);
        }
    };

    useEffect(() => {
    if (selectedTab === 'log') {
        const fetchLogs = async () => {
            setLogTableLoading(true);
            try {
                const response = await fetch(
                    `${API_BASE_URL}/admin/traffic-logs/${deviceId}?` +
                    `fromYear=${logDateFilters.from.year}&fromMonth=${logDateFilters.from.month}&fromDay=${logDateFilters.from.day}&` +
                    `toYear=${logDateFilters.to.year}&toMonth=${logDateFilters.to.month}&toDay=${logDateFilters.to.day}&` +
                    `limit=${logLimit}`,
                    {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    }
                );
                
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                if (response.status === 401) {
                    navigate('/unauthorized');
                    return;
                }
                const data = await response.json();

                setLogEntries(data);
            } catch (error) {
                console.error('Error fetching logs:', error);
                setError('Failed to load traffic logs');
            } finally {
                setLogTableLoading(false);
            }
        };
        
        fetchLogs();
        }
    }, [selectedTab, logDateFilters, deviceId, logLimit]);

    useEffect(() => {
        if (selectedTab === 'battery') {
            const fetchChartData = async () => {
                setIsChartLoading(true);
                try {
                    const response = await fetch(
                        `${API_BASE_URL}/admin/device/battery-chart/${deviceId}?interval=${selectedInterval}`, 
                        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
                    );
                    const data = await response.json();
                    const processedData = data.map(item => ({
                    ...item,
                    balanceCount: item.balance.filter(cell => cell === '1').length,
                    protectionCount: Object.values(item.protection).filter(val => val === 1).length
                    }))
                    setChartData(processedData);
                    console.log('Chart data:', data);
                } catch (error) {
                    console.error('Error fetching chart data:', error);
                } finally {
                    setIsChartLoading(false);
                }
            };
            fetchChartData();
        } else if (selectedTab === 'log') {      
            fetchChart();
        }
    }, [selectedTab, deviceId, selectedInterval]);

    useEffect(() => {
        fetchChart();
    }, [timeRange]);

    function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = payload[0].payload;

  const balancingCells = data.balance
    ? data.balance
        .map((val, idx) => (val === '1' ? idx : null))
        .filter((v) => v !== null)
    : [];

  const activeProtections = data.protection
  ? Object.entries(data.protection)
      .filter(([_, val]) => val)
      .map(([key]) => {
        const spaced = key.replace(/([A-Z])/g, ' $1').trim();
        return spaced.charAt(0).toUpperCase() + spaced.slice(1);
      })
  : [];

  // Define colors for balancing cells and protections (choose your colors)
  const balanceColor = '#058fa1';
  const protectionColor = '#ff6666';

  return (
    <div style={{ backgroundColor: '#ffffff', padding: 10, borderRadius: 4, color: '#505050' }}>
      <div><strong>{new Date(label).toLocaleString()}</strong></div>

      {payload.map((entry) => {
        // Skip keys related to balance and protection to avoid duplication
        if (entry.dataKey === 'balance' || entry.dataKey === 'protection' || entry.name === 'Balancing Cells' || entry.name === 'Active Protections') {
          return null;
        }
        return (
          <div key={entry.dataKey} style={{ color: entry.color, marginTop: 4 }}>
            {entry.name}: {entry.value}
          </div>
        );
      })}

      <div style={{ marginTop: 8, color: balanceColor }}>
        Balancing Cells: {balancingCells.length > 0 ? balancingCells.join(', ') : 'None'}
      </div>

      <div style={{ marginTop: 8, color: protectionColor }}>
        Active Protections: {activeProtections.length > 0 ? activeProtections.join(', ') : 'None'}
      </div>
    </div>
  );
}

    const handleTabChange = async (event, newValue) => {
        if (isChanged) {
            const confirmDiscard = window.confirm('You have unsaved changes. Do you want to discard them?');
            if (!confirmDiscard) return;
        }
        setSelectedTab(newValue);
        setIsChanged(false);
        setTabLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/device/${newValue}/${deviceId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            if (response.status === 401) {
                navigate('/unauthorized');
                return;
            }
            const data = await response.json();
            console.log(data); // debug
            setTabData(data);
            setOriginalTabData(data);
        } catch (error) {
            setError(error.message);
        } finally {
            setTabLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTabData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        setIsChanged(true);
    };

    const handleInputChangeText = (e) => {
        const { name, value } = e.target;
        const nameParts = name.split('.');
        setTabData((prevData) => ({
            ...prevData,
            [nameParts[0]]: {
                ...prevData[nameParts[0]],
                [nameParts[1]]: value
            }
        }));
        setIsChanged(true);
    };

    const handleOwnerChange = (e) => {
        const selectedUser = tabData.users.find(user => user.id === e.target.value);
        setTabData((prevData) => ({
            ...prevData,
            owner: selectedUser,
        }));
        setIsChanged(true);
    };

        const handleSave = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/device/info/${deviceId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(tabData)
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            if (response.status === 401) {
                navigate('/unauthorized');
                return;
            }
            const data = await response.json();
            setOriginalTabData(data);
            setIsChanged(false);
        } catch (error) {
            setError(error.message);
        }
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const handleDeleteFile = async (file, disk, setFileList) => {
        if (!file || `${file.fileName}.${file.fileExtension}` !== fileNameToDelete) {
            setDeleteError('File name does not match');
            return;
        }
        
        try {
            setIsDeleting(true);
            const response = await fetch(`${API_BASE_URL}/admin/delete-file/${deviceId}/${encodeURIComponent(fileNameToDelete)}/${diskToDelete}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete file');
            }
            if (response.status === 401) {
                navigate('/unauthorized');
                return;
            }
            
            setFileList(prev => prev.filter(f => f.fileName !== file.fileName));
            setDeleteConfirmOpen(false);
            setDeleteSuccess(true);
            setDeleteError(null);

        } catch (error) {
            setDeleteError(error.message);
            setDeleteSuccess(false);
        } finally {
            setIsDeleting(false);
            setFileNameToDelete('');
        }
    };

    const handleDownloadUserFile = async (file, disk) => {
        setIsDownloading(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/admin/download-file/${deviceId}/${encodeURIComponent(`${file.fileName}.${file.fileExtension}`)}/01`,
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }
            );
            
            if (!response.ok) throw new Error('Download failed');

            if (response.status === 401) {
                navigate('/unauthorized');
                return;
            }
            
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `${file.fileName}.${file.fileExtension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Download error:', error);
            setError('File download failed');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleForceUserLog = async (file) => {
        try {
            setForceLogError(null);
            const response = await fetch(
                `${API_BASE_URL}/admin/force-log/${deviceId}/${encodeURIComponent(`${file.fileName}.${file.fileExtension}`)}`,
                {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }
            );
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Force log failed');
            }
            
            setForceLogSuccess(true);
            console.log('Log forced successfully');

        } catch (error) {
            console.error('Force log error:', error);
            setForceLogError(error.message);
        }
    };

    const handleUpgradeFile = async (file, type) => {
        if (!file) return;

        setIsUpgrading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const endpoint = type === 'app' ? 'upgrade-app' : 'upgrade-bios';
            const response = await fetch(`${API_BASE_URL}/admin/${endpoint}/${deviceId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Upgrade failed');

            alert(`${type.toUpperCase()} upgrade started successfully`);
        } catch (error) {
            console.error(`Error upgrading ${type}:`, error);
            alert(error.message);
        } finally {
            setIsUpgrading(false);
        }
    };

    const DateRangeFilter = () => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        
        // Generate year options (last 10 years)
        const years = Array.from({length: 10}, (_, i) => currentYear - i);
        
        // Get days in month helper
        const getDaysInMonth = (year, month) => {
            return new Date(year, month, 0).getDate();
        };

        return (
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Select
                        value={logDateFilters.from.year}
                        onChange={(e) => setLogDateFilters(prev => ({
                            ...prev,
                            from: {...prev.from, year: e.target.value}
                        }))}
                        size="small"
                    >
                        {years.map(year => (
                            <MenuItem key={year} value={year}>{year}</MenuItem>
                        ))}
                    </Select>
                    <Select
                        value={logDateFilters.from.month}
                        onChange={(e) => setLogDateFilters(prev => ({
                            ...prev,
                            from: {...prev.from, month: e.target.value}
                        }))}
                        size="small"
                    >
                        {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                            <MenuItem key={month} value={month}>
                                {new Date(0, month - 1).toLocaleString('default', {month: 'short'})}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        value={logDateFilters.from.day}
                        onChange={(e) => setLogDateFilters(prev => ({
                            ...prev,
                            from: {...prev.from, day: e.target.value}
                        }))}
                        size="small"
                    >
                        {Array.from({length: getDaysInMonth(logDateFilters.from.year, logDateFilters.from.month)}, (_, i) => i + 1).map(day => (
                            <MenuItem key={day} value={day}>{day}</MenuItem>
                        ))}
                    </Select>
                </Box>
                
                <Typography>to</Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {/* Repeat the same structure for "to" date */}
                    <Select
                        value={logDateFilters.to.year}
                        onChange={(e) => setLogDateFilters(prev => ({
                            ...prev,
                            to: {...prev.to, year: e.target.value}
                        }))}
                        size="small"
                    >
                        {years.map(year => (
                            <MenuItem key={year} value={year}>{year}</MenuItem>
                        ))}
                    </Select>
                    <Select
                        value={logDateFilters.to.month}
                        onChange={(e) => setLogDateFilters(prev => ({
                            ...prev,
                            to: {...prev.to, month: e.target.value}
                        }))}
                        size="small"
                    >
                        {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                            <MenuItem key={month} value={month}>
                                {new Date(0, month - 1).toLocaleString('default', {month: 'short'})}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        value={logDateFilters.to.day}
                        onChange={(e) => setLogDateFilters(prev => ({
                            ...prev,
                            to: {...prev.to, day: e.target.value}
                        }))}
                        size="small"
                    >
                        {Array.from({length: getDaysInMonth(logDateFilters.to.year, logDateFilters.to.month)}, (_, i) => i + 1).map(day => (
                            <MenuItem key={day} value={day}>{day}</MenuItem>
                        ))}
                    </Select>
                     <TextField
                        select
                        label="Limit"
                        value={logLimit}
                        onChange={(e) => setLogLimit(parseInt(e.target.value))}
                        size="small"
                        sx={{ width: 120 }}
                    >
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                        <MenuItem value={1000}>1,000</MenuItem>
                        <MenuItem value={2500}>2,500</MenuItem>
                        <MenuItem value={5000}>5,000</MenuItem>
                        <MenuItem value={10000}>10,000</MenuItem>
                        <MenuItem value={20000}>20,000</MenuItem>
                        <MenuItem value={50000}>50,000</MenuItem>
                    </TextField>
                </Box>
            </Box>
        );
    };

    const handleEditClick = (log) => {
        setEditingLogId(log.id);
        setEditedLogData({ ...log });
    };

    const handleSaveClick = async (logId) => {
        console.log('Saving log:', editedLogData);
        try {
            const response = await fetch(
                `${API_BASE_URL}/admin/service-notes/${logId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(editedLogData)
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            
            if (response.status === 200) {
                alert('Service note updated successfully');
                    setTabData(prevData =>
                    prevData.map(log =>
                    log.id === logId ? editedLogData : log
                    )
                );
                setEditingLogId(null);
                setEditedLogData({});
            } else {
                alert(`HTTP error! status: ${response.status}`);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } catch (error) {
        console.error('Error saving service note:', error);
        }
    };

    const handleDeleteClick = (logId) => {
        setLogToDelete(logId);
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/admin/service-notes/${logToDelete}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

        setTabData(prevData => prevData.filter(log => log.id !== logToDelete));
        setOpenDeleteDialog(false);
        setLogToDelete(null);
        alert('Service note deleted successfully');
        
        } catch (error) {
        console.error('Error deleting service note:', error);
        alert(`Error deleting service note: ${error.message}`);
        }
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setLogToDelete(null);
    };

    const handleFieldChange = (e, fieldName) => {
        setEditedLogData(prev => ({
        ...prev,
        [fieldName]: e.target.value
        }));
    };

    const getSeverityLabel = (severity) => {
        switch (severity) {
        case 'green':
            return 'Good';
        case 'yellow':
            return 'Warning';
        case 'red':
            return 'Fatal';
        default:
            return 'Retired'; 
        }
    };

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        
        const newImages = await Promise.all(files.map(async (file) => {
            const options = {
                maxSizeMB: 0.02,
                maxWidthOrHeight: 800,
                useWebWorker: true,
                fileType: 'image/jpeg',
                initialQuality: 0.6,
                alwaysKeepResolution: false
            };
            
            const compressedFile = await imageCompression(file, options);
            
            // Convert compressed file to base64
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(compressedFile);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });

            return {
                id: -1,
                noteId: editedLogData.id,
                file: compressedFile,
                name: file.name,
                size: compressedFile.size,
                data: base64,
                originalSize: file.size,
                compressedSize: compressedFile.size
            };     
        }));
        
        setEditedLogData(prev => ({
            ...prev,
            images: [...(prev.images || []), ...newImages],
        }));
    };

    const handleRemoveImage = (imageId) => {
        setEditedLogData(prev => ({
        ...prev,
        images: (prev.images || []).filter(img => img.id !== imageId),
        }));
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Box sx={{typography: 'body1', marginTop: '64px'}}>
            <Typography variant="h4" component="div" sx={{ color: deviceDetails?.connected ? 'green' : 'red', backgroundColor: '#ffffff', padding: 2 }}>
                {deviceDetails.name} ({deviceDetails.type})
            </Typography>
            <Tabs value={selectedTab} onChange={handleTabChange} disabled={tabLoading} aria-label="device details tabs" sx={{ backgroundColor: '#e6e6e6', color: 'white' }}>
                <Tab label="Info" value="info" sx={{ color: '#000000' }} />
                <Tab label="Status" value="status" sx={{ color: '#000000' }} />
                <Tab label="Battery" value="battery" sx={{ color: '#000000' }} />
                <Tab label="Screen" value="screen" sx={{ color: '#000000' }} />
                <Tab label="File System" value="file" sx={{ color: '#000000' }} />
                <Tab label="Log" value="log" sx={{ color: '#000000' }} />
                <Tab label="Service" value="service" sx={{ color: '#000000' }} />
            </Tabs>
            <Box sx={{ padding: 2 }}>
                {tabLoading ? (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '300px',
                        color: '#ffffff',
                        bgcolor: '#e6e6e6 !important'
                    }}>
                        <CircularProgress />
                    </Box>
                ) : <>
                    {selectedTab === 'info' && tabData && (
                        <Grid container spacing={2} paddingRight={2} sx={{ backgroundColor: '#ffffff !important', width: '100%' }}>
                            {/* Address Section */}
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, border: '1px solid #9c9c9c', borderRadius: 1 }}>
                                    <Typography variant="h6" gutterBottom>Address Information</Typography>
                                    <TextField
                                        label="Street"
                                        name="address.street"
                                        value={tabData.address?.street || ''}
                                        onChange={handleInputChangeText}
                                        fullWidth
                                        margin="normal"
                                    />
                                    <TextField
                                        label="Suburb"
                                        name="address.suburb"
                                        value={tabData.address?.suburb || ''}
                                        onChange={handleInputChangeText}
                                        fullWidth
                                        margin="normal"
                                    />
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="State"
                                                name="address.state"
                                                value={tabData.address?.state || ''}
                                                onChange={handleInputChangeText}
                                                fullWidth
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Postcode"
                                                name="address.postcode"
                                                value={tabData.address?.postcode || ''}
                                                onChange={handleInputChangeText}
                                                fullWidth
                                                margin="normal"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Country"
                                                name="address.country"
                                                value={tabData.address?.country || ''}
                                                onChange={handleInputChangeText}
                                                fullWidth
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                        <TextField
                                            select
                                            label="Direction"
                                            name="direction"
                                            value={tabData.direction}
                                            onChange={handleInputChange}
                                            fullWidth
                                            margin="normal"
                                        >
                                            <MenuItem value="Eastbound">Eastbound</MenuItem>
                                            <MenuItem value="Westbound">Westbound</MenuItem>
                                            <MenuItem value="Northbound">Northbound</MenuItem>
                                            <MenuItem value="Southbound">Southbound</MenuItem>
                                            <MenuItem value="N/A">N/A</MenuItem>
                                        </TextField>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Latitude"
                                                name="location.lat"
                                                value={tabData.location?.lat || ''}
                                                onChange={handleInputChangeText}
                                                fullWidth
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Longitude"
                                                name="location.lon"
                                                value={tabData.location?.lon || ''}
                                                onChange={handleInputChangeText}
                                                fullWidth
                                                margin="normal"
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                            {/* Hardware Section */}
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, border: '1px solid #9c9c9c', borderRadius: 1 }}>
                                    <Typography variant="h6" gutterBottom>Device Details</Typography>
                                    <TextField
                                        label="Description"
                                        name="description"
                                        value={tabData.description}
                                        onChange={handleInputChange}
                                        fullWidth
                                        margin="normal"
                                        multiline
                                        rows={3}
                                    />
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                select
                                                label="Device Type"
                                                name="type"
                                                value={tabData.type}
                                                onChange={handleInputChange}
                                                fullWidth
                                                margin="normal"
                                            >
                                                {tabData.deviceTypes.map((type) => (
                                                    <MenuItem key={type.id} value={type.name}>
                                                        {type.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Built Date"
                                                name="builtDate"
                                                value={new Date(tabData.builtDate).toISOString().substring(0, 10)}
                                                onChange={handleInputChange}
                                                fullWidth
                                                margin="normal"
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Warranty Date"
                                                name="warrantyDate"
                                                value={new Date(tabData.warranty).toISOString().substring(0, 10)}
                                                onChange={handleInputChange}
                                                fullWidth
                                                margin="normal"
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Service Plan Date"
                                                name="servicePlanDate"
                                                value={new Date(tabData.servicePlan).toISOString().substring(0, 10)}
                                                onChange={handleInputChange}
                                                fullWidth
                                                margin="normal"
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                select
                                                label="Battery Size"
                                                name="batterySize"
                                                value={tabData.batterySize}
                                                onChange={handleInputChange}
                                                fullWidth
                                                margin="normal"
                                            >
                                                {tabData.batterySizes.map((size) => (
                                                    <MenuItem key={size.id} value={size.capacity}>
                                                        {size.capacity} Ah
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                select
                                                label="Solar Size"
                                                name="solarSize"
                                                value={tabData.solarSize}
                                                onChange={handleInputChange}
                                                fullWidth
                                                margin="normal"
                                            >
                                                {tabData.solarSizes.map((size) => (
                                                    <MenuItem key={size.id} value={size.watt}>
                                                        {size.watt} W
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Owner Section */}
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, border: '1px solid #9c9c9c', borderRadius: 1 }}>
                                    <Typography variant="h6" gutterBottom>Owner Information</Typography>
                                    <TextField
                                        select
                                        label="Select Owner"
                                        value={tabData.owner?.id || ''}
                                        onChange={handleOwnerChange}
                                        fullWidth
                                        margin="normal"
                                    >
                                        {tabData.users.map((user) => (
                                            <MenuItem key={user.id} value={user.id}>
                                                {user.orgName}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    
                                    {tabData.owner && (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="subtitle1">{tabData.owner.orgName}</Typography>
                                            <Typography variant="body2">User Name: {tabData.owner.firstName} {tabData.owner.lastName}</Typography>
                                            <Typography variant="body2">User Email: {tabData.owner.username}</Typography>
                                            <Typography variant="body2">Org. Email: {tabData.owner.email}</Typography>
                                            <Typography variant="body2">Org. Number: {tabData.owner.phoneNumber}</Typography>
                                            <Typography variant="body2">Active: {tabData.owner.active ? 'Yes' : 'No'}</Typography>
                                            <Typography variant="body2">Data User Enabled: {tabData.owner.dataUserEnabled ? 'Yes' : 'No'}</Typography>
                                            <Typography variant="body2">User ID: {tabData.owner.id}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            {/* Photo Section */}
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, border: '1px solid #9c9c9c', borderRadius: 1 }}>
                                    <Typography variant="h6" gutterBottom>Device Photo</Typography>
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="raised-button-file"
                                        type="file"
                                    />
                                    <label htmlFor="raised-button-file">
                                        <Button variant="contained" component="span" sx={{ mb: 2 }}>
                                            Upload Photo
                                        </Button>
                                    </label>
                                    <Box sx={{ 
                                        width: '100%', 
                                        height: 200, 
                                        bgcolor: '#f5f5f5',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {tabData.photo ? 'Preview' : 'No image available'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={!isChanged}
                                    sx={{ mt: 2 }}
                                >
                                    Save Changes
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                    
                    {selectedTab === 'status' && tabData && (
                        <Grid container spacing={2} paddingRight={2} paddingBottom={2} sx={{ backgroundColor: '#ffffff !important', width: '100%' }}>
                            {/* Connection Status */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 2, 
                                    border: '1px solid #9c9c9c', 
                                    borderRadius: 1, 
                                    bgcolor: '#f5f5f5'
                                }}>
                                    <Typography variant="h6" gutterBottom>Connection Status</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Online:</Typography>
                                            <Typography variant="body1" sx={{ color: tabData.live ? '#32f73b' : '#b10b0b' }}>
                                                {tabData.live ? 'Connected' : 'Disconnected'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Operation:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>
                                                {tabData.running_status ? 'Operating' : 'Power-Saving'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Last Update:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>
                                                {new Date(new Date(tabData.updated_at).getTime() + 36000000).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Auto Timezone:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>
                                                {tabData.auto_time_zone ? 'Yes' : 'No'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Auto Sync:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>
                                                {tabData.auto_sync_on_boot ? 'Yes' : 'No'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Timezone:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>
                                                {tabData.timezone}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Paper sx={{ 
                                                p: 2, 
                                                border: '1px solid #9c9c9c', 
                                                borderRadius: 1, 
                                                color: 'white',
                                                bgcolor: '#f5f5f5'
                                            }}>
                                                <Typography variant="h6" gutterBottom sx={{ color: '#2e2e2e' }}>Device Time</Typography>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Button 
                                                        variant="contained" 
                                                        onClick={async () => {
                                                            try {
                                                                const response = await fetch(`${API_BASE_URL}/admin/device-time/${deviceId}`, {
                                                                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                                });
                                                                const data = await response.json();
                                                                setDeviceTime(data);
                                                            } catch (error) {
                                                                console.error('Error fetching device time:', error);
                                                            }
                                                        }}
                                                    >
                                                        Read from Screen
                                                    </Button>
                                                    {deviceTime ? (
                                                        <Typography variant="body1" sx={{ color: '#000' }}>
                                                          {`${deviceTime.year}/${deviceTime.month}/${deviceTime.day} ${deviceTime.hour}:${deviceTime.minute}:${deviceTime.second}, ${deviceTime.dayOfWeek}`}
                                                        </Typography>
                                                    ) : <Typography variant="body1" sx={{ color: '#000' }}>
                                                            --/--/---- --:--:--
                                                        </Typography>}
                                                    
                                                </Box>
                                                
                                                <Box component="form" sx={{ mt: 2 }} onSubmit={async (e) => {
                                                    e.preventDefault();
                                                    const formData = new FormData(e.currentTarget);
                                                    try {
                                                        await fetch(`${API_BASE_URL}/admin/sync-time/${deviceId}`, {
                                                            method: 'POST',
                                                            headers: {
                                                                'Content-Type': 'application/json',
                                                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                                                            },
                                                            body: JSON.stringify({
                                                                second: formData.get('second'),
                                                                minute: formData.get('minute'),
                                                                hour: formData.get('hour'),
                                                                day: formData.get('day'),
                                                                month: formData.get('month'),
                                                                year: formData.get('year'),
                                                                dayOfWeek: formData.get('dayOfWeek')
                                                            })
                                                        });
                                                    } catch (error) {
                                                        console.error('Error syncing time:', error);
                                                    }
                                                }}>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={12}>
                                                            <Button type="submit" variant="contained" color="primary">
                                                                Set to Screen
                                                            </Button>
                                                        </Grid>
                                                        <Grid item>
                                                            <TextField 
                                                                sx={{ width: 100 }}
                                                                label="Year"
                                                                name="year"
                                                                type="number"
                                                                inputProps={{ min: 0, max: 99 }}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <TextField 
                                                                sx={{ width: 100 }}
                                                                label="Month" 
                                                                name="month"
                                                                inputProps={{ min: 0, max: 99 }}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <TextField 
                                                                sx={{ width: 100 }}
                                                                label="Day"
                                                                name="day"
                                                                inputProps={{ min: 0, max: 99 }}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <TextField 
                                                                sx={{ width: 100 }}
                                                                label="Hour"
                                                                name="hour"
                                                                inputProps={{ min: 0, max: 99 }}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <TextField 
                                                                sx={{ width: 100 }}
                                                                label="Min"
                                                                name="minute"
                                                                inputProps={{ min: 0, max: 99 }}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <TextField 
                                                                sx={{ width: 100 }}
                                                                label="Sec"
                                                                name="second"
                                                                inputProps={{ min: 0, max: 99 }}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <TextField 
                                                                select
                                                                label="Week Day"
                                                                name="dayOfWeek"
                                                                sx={{ minWidth: 100 }}
                                                            >
                                                                <MenuItem value={0}>Sunday</MenuItem>
                                                                <MenuItem value={1}>Monday</MenuItem>
                                                                <MenuItem value={2}>Tuesday</MenuItem>
                                                                <MenuItem value={3}>Wednesday</MenuItem>
                                                                <MenuItem value={4}>Thursday</MenuItem>
                                                                <MenuItem value={5}>Friday</MenuItem>
                                                                <MenuItem value={6}>Saturday</MenuItem>
                                                            </TextField>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* Network Details */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 2, 
                                    border: '1px solid #9c9c9c', 
                                    borderRadius: 1, 
                                    color: 'white',
                                    bgcolor: '#f5f5f5'
                                }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#2e2e2e' }}>Network Details</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>IP Address:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.ip}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Port:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.port}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Signal Strength:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.dbm} dBm</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Network Type:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.network_type}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>IMEI:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.imei}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>SIM Number:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.sim_number}</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>IMSI:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.imsi}</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>ICCID:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.iccid}</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* Location & Security */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 2, 
                                    border: '1px solid #9c9c9c', 
                                    borderRadius: 1, 
                                    color: 'white',
                                    bgcolor: '#f5f5f5'
                                }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#2e2e2e' }}>Location & Security</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Coordinates:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>
                                                {tabData.lat_number}°{tabData.lat_letter}, {tabData.lon_number}°{tabData.lon_letter}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Door Status:</Typography>
                                            <Typography variant="body1" sx={{ color: tabData.door ? '#32f73b' : '#b10b0b' }}>
                                                {tabData.door ? 'Open' : 'Closed'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* Tilt Sensors */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 2, 
                                    border: '1px solid #9c9c9c', 
                                    borderRadius: 1, 
                                    color: 'white',
                                    bgcolor: '#f5f5f5'
                                }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: '#2e2e2e' }}>Tilt Sensors</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>X-axis:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.tilt_x}°</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Y-axis:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.tilt_y}°</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Z-axis:</Typography>
                                            <Typography variant="body1" sx={{ color: '#000' }}>{tabData.tilt_z}°</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                    {selectedTab === 'battery' && tabData && (
                        <Grid container spacing={2}>
                            {/* Chart Section */}
                            <Grid item xs={12}>
                            <Paper sx={{ 
                                p: 2, 
                                border: '1px solid #9c9c9c', 
                                borderRadius: 1, 
                                bgcolor: '#f5f5f5',
                                height: 500
                            }}>
                                <Box display="flex" justifyContent="flex-end" mb={2}>
                                <TextField
                                    select
                                    label="Interval"
                                    value={selectedInterval}
                                    onChange={(e) => setSelectedInterval(e.target.value)}
                                    sx={{ width: 120, backgroundColor: '#c7c7c7' }}
                                    size="small"
                                >
                                    <MenuItem value="24h">24 Hours</MenuItem>
                                    <MenuItem value="7d">7 Days</MenuItem>
                                    <MenuItem value="30d">30 Days</MenuItem>
                                </TextField>
                                </Box>
                                
                                {isChartLoading ? (
                                <Box sx={{ 
                                    height: 400, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center' 
                                }}>
                                    <CircularProgress />
                                </Box>
                                ) : chartData?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={400} backgroundColor>
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#dbdbdb" />
                                        <XAxis
                                            dataKey="timestamp"
                                            tick={{ fill: '#505050' }}
                                            tickFormatter={(timeStr) => 
                                            new Date(new Date(timeStr).getTime()).toLocaleTimeString([], {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })
                                            }
                                        />
                                        <YAxis yAxisId="left" tick={{ fill: '#505050' }} domain={[0, 100]} />
                                        <YAxis
                                            yAxisId="right"
                                            orientation="right"
                                            tick={{ fill: '#505050' }}
                                            domain={[-15, 15]}
                                            mirror={false}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend wrapperStyle={{ paddingTop: 20, color: '#505050' }} />
                                            <Line
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="rsoc"
                                                stroke="#ff7300"
                                                strokeWidth={2}
                                                dot={false}
                                                name="State of Charge (%)"
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="current"
                                                stroke="#8884d8"
                                                strokeWidth={2}
                                                dot={false}
                                                name="Current (A)"
                                            />
                                            <Line
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="temperature"
                                                stroke="#ffc658"
                                                strokeWidth={2}
                                                dot={false}
                                                name="Temperature (°C)"
                                            />
                                            <Line
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="voltage"
                                                stroke="#82ca9d"
                                                strokeWidth={2}
                                                dot={false}
                                                name="Voltage (V)"
                                            />
                                            <Line
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="balanceCount"
                                                stroke="#058fa1"
                                                strokeWidth={1}
                                                dot={false}
                                                name="Balancing Cells"
                                            />
                                            <Line
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="protectionCount"
                                                stroke="#ff6666"
                                                strokeWidth={1}
                                                dot={false}
                                                name="Active Protections"
                                            />
                                            </LineChart>
                                        </ResponsiveContainer>
                                        ) : (
                                        <Box sx={{ 
                                            height: 400, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            color: '#666'
                                        }}>
                                            <Typography>No battery data available</Typography>
                                        </Box>
                                        )}
                                    </Paper>
                                </Grid>

                                {/* Main Battery Metrics */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ 
                                        p: 2, 
                                        border: '1px solid #505050', 
                                        borderRadius: 1, 
                                        color: '#505050',
                                        bgcolor: '#ffffff'
                                    }}>
                                        <Typography variant="h6" gutterBottom>Latest Status</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Voltage:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.voltage} V
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Current:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.current} A
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>State of Charge:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.stateOfCharge}%
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Temperature:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.temperature}°C
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Capacity & Cycles */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ 
                                        p: 2, 
                                        border: '1px solid #505050', 
                                        borderRadius: 1, 
                                        bgcolor: '#ffffff'
                                    }}>
                                        <Typography variant="h6" gutterBottom>Capacity</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Total:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.capacity?.max || 'N/A'} mA
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Remaining:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.capacity?.remaining || 'N/A'} mA
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Cycles:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.cycles || 'N/A'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>FET Status:</Typography>
                                                <Typography variant="body1" sx={{ color: tabData.fetStatus === 3 ? '#32f73b' : '#b10b0b' }}>
                                                    {tabData.fetStatus === 3 ? 'Operating' : 'Protecting'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Balance Cells */}
                                <Grid item xs={12}>
                                    <Paper sx={{ 
                                        p: 2, 
                                        border: '1px solid #505050', 
                                        borderRadius: 1, 
                                        bgcolor: '#ffffff'
                                    }}>
                                        <Typography variant="h6" gutterBottom>Cell Balance</Typography>
                                        <Grid container spacing={1} justifyContent="center">
                                            {tabData.balance?.map((cell, index) => (
                                                <Grid item key={index}>
                                                    <Box sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '4px',
                                                        bgcolor: cell === '1' ? '#32f73b' : '#d4d4d4',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: '1px solid #404040'
                                                    }}>
                                                        <Typography variant="caption" sx={{ 
                                                            color: cell === '1' ? '#1a1a1a' : '#757575',
                                                            fontWeight: 600 
                                                        }}>
                                                            {index}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Protection Status */}
                                <Grid item xs={12}>
                                    <Paper sx={{ 
                                        p: 2, 
                                        border: '1px solid #505050', 
                                        borderRadius: 1, 
                                        bgcolor: '#ffffff'
                                    }}>
                                        <Typography variant="h6" gutterBottom>Protection Status</Typography>
                                        <Grid container spacing={2}>
                                            {Object.entries(tabData?.protection)?.map(([key, value]) => (
                                                <Grid item xs={6} md={4} key={key}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {value ? (
                                                            <CheckCircleIcon sx={{ color: '#32f73b', fontSize: 20 }} />
                                                        ) : (
                                                            <CancelIcon sx={{ color: '#b10b0b', fontSize: 20 }} />
                                                        )}
                                                        <Typography variant="body2">
                                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Additional Info */}
                                <Grid item xs={12}>
                                    <Paper sx={{ 
                                        p: 2, 
                                        border: '1px solid #505050', 
                                        borderRadius: 1, 
                                        bgcolor: '#ffffff'
                                    }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Battery ASCII:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000', fontFamily: 'monospace' }}>
                                                    {tabData.asciiStatus}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Last Updated:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {new Date(tabData.lastUpdated).toLocaleString()}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                        {selectedTab === 'screen' && tabData && (
                            <Grid container spacing={2}>
                                {/* Screen Specifications */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ 
                                        p: 2, 
                                        border: '1px solid #505050', 
                                        borderRadius: 1, 
                                        bgcolor: '#ffffff'
                                    }}>
                                        <Typography variant="h6" gutterBottom>Screen Specifications</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Width:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.width} pixels
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Height:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.height} pixels
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>Color Order:</Typography>
                                                <Typography variant="body1" sx={{ color: '#000' }}>
                                                    {tabData.colorOrder}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Screen Controls */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ 
                                        p: 2, 
                                        border: '1px solid #505050', 
                                        borderRadius: 1, 
                                        bgcolor: '#ffffff'
                                    }}>
                                        <Typography variant="h6" gutterBottom>Screen Controls</Typography>
                                        <Box display="flex" gap={2} flexWrap="wrap">
                                        <Button 
                                                variant="contained" 
                                                onClick={async () => {
                                                    setScreenLoading(true);
                                                    try {
                                                        const response = await fetch(`${API_BASE_URL}/admin/screen-status/${deviceId}`, {
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        const data = await response.json();
                                                        if (data.status === 1 || data.status === 0) {
                                                            setScreenStatus(data.status);
                                                        } else {
                                                            setScreenStatus(-1);
                                                        }
                                                    } catch (error) {
                                                        console.error('Error checking screen status:', error);
                                                    } finally {
                                                        setScreenLoading(false);
                                                    }
                                                }}
                                                disabled={isScreenLoading}
                                            >
                                                {isScreenLoading ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Check Screen Status'
                                                )}
                                            </Button>
                                            <Button 
                                                variant="contained" 
                                                color="success"
                                                onClick={async () => {
                                                    try {
                                                        await fetch(`${API_BASE_URL}/admin/screen-on/${deviceId}`, {
                                                            method: 'POST',
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        setScreenStatus(1);
                                                    } catch (error) {
                                                        console.error('Error turning screen on:', error);
                                                    }
                                                }}
                                            >
                                                Screen On
                                            </Button>
                                            <Button 
                                                variant="contained" 
                                                color="error"
                                                onClick={async () => {
                                                    try {
                                                        await fetch(`${API_BASE_URL}/admin/screen-off/${deviceId}`, {
                                                            method: 'POST',
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        setScreenStatus(0);
                                                    } catch (error) {
                                                        console.error('Error turning screen off:', error);
                                                    }
                                                }}
                                            >
                                                Screen Off
                                            </Button>
                                            <Button 
                                                variant="contained" 
                                                color="success"
                                                onClick={async () => {
                                                    try {
                                                        await fetch(`${API_BASE_URL}/admin/wigwag-on/${deviceId}`, {
                                                            method: 'POST',
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        setWigWagStatus(1);
                                                    } catch (error) {
                                                        console.error('Error turning wig wag on:', error);
                                                    }
                                                }}
                                            >
                                                Wig Wag On
                                            </Button>
                                            <Button 
                                                variant="contained" 
                                                color="error"
                                                onClick={async () => {
                                                    try {
                                                        await fetch(`${API_BASE_URL}/admin/wigwag-off/${deviceId}`, {
                                                            method: 'POST',
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        setWigWagStatus(0);
                                                    } catch (error) {
                                                        console.error('Error turning wig wag off:', error);
                                                    }
                                                }}
                                            >
                                                Wig Wag Off
                                            </Button>
                                        </Box>
                                        {screenStatus !== null && (
                                            <Typography sx={{ 
                                                mt: 2, 
                                                color: screenStatus === 1 ? '#81c784' : 
                                                    screenStatus === 0 ? '#e57373' : 
                                                    '#ffb74d' 
                                            }}>
                                                {screenStatus === 1 ? 'Screen is ON' :
                                                screenStatus === 0 ? 'Screen is OFF' : 
                                                'Unable to connect to screen'}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                        {selectedTab === 'file' && (
                        <Grid container spacing={2}>
                            {/* System Versions Section */}
                                <Grid item xs={12}>
                                    <Paper sx={{ 
                                        p: 2, 
                                        border: '1px solid #505050', 
                                        borderRadius: 1, 
                                        bgcolor: '#ffffff'
                                    }}>
                                        <Typography variant="h6" gutterBottom>System</Typography>
                                        <Box display="flex" gap={2} alignItems="center">
                                            <Button 
                                                variant="contained" 
                                                onClick={async () => {
                                                    setVersionLoading(true);
                                                    try {
                                                        const response = await fetch(`${API_BASE_URL}/admin/system-versions/${deviceId}`, {
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        const data = await response.json();
                                                        setSystemVersions(data);
                                                    } catch (error) {
                                                        console.error('Error fetching system versions:', error);
                                                    } finally {
                                                        setVersionLoading(false);
                                                    }
                                                }}
                                                disabled={isVersionLoading}
                                            >
                                                {isVersionLoading ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Get System Versions'
                                                )}
                                            </Button>
                                            <Button 
                                                variant="contained" 
                                                onClick={async () => {
                                                    try {
                                                        const response = await fetch(`${API_BASE_URL}/admin/restart-system/${deviceId}`, {
                                                            method: 'POST',
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        const data = await response.json();
                                                        if (response.ok) {
                                                            alert('System restart initiated successfully. The system may take a few minutes to reboot and reconnect to the server.');
                                                        } else {
                                                            alert(`Failed to restart system: ${data.message || 'Unknown error'}`);
                                                        }
                                                    } catch (error) {
                                                        console.error('Error restarting system:', error);
                                                        alert('Error restarting system. Please try again later.');
                                                    }
                                                }}
                                            >
                                                Restart System
                                            </Button>
                                            <Button 
                                                variant="contained" 
                                                onClick={async () => {
                                                    try {
                                                        const response = await fetch(`${API_BASE_URL}/admin/generate-log-file/${deviceId}`, {
                                                            method: 'POST',
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        const data = await response.json();
                                                        if (response.ok) {
                                                            alert('Log file generation initiated successfully.');
                                                        } else {
                                                            alert(`Failed to generate log file: ${data.message || 'Unknown error'}`);
                                                        }
                                                    } catch (error) {
                                                        console.error('Error generating log file:', error);
                                                        alert('Error generating log file. Please try again later.');
                                                    }
                                                }}
                                            >
                                                ** Generate Log file **
                                            </Button>
                                            
                                            {systemVersions && (
                                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                                    {[
                                                        { label: 'App Version', key: 'app' },
                                                        { label: 'Bios Version', key: 'bios' },
                                                        { label: 'Logic Version', key: 'logic' },
                                                        { label: 'Nxp Version', key: 'nxp' },

                                                    ].map((item) => (
                                                        <Grid item xs={6} md={4} key={item.key}>
                                                            <Box display="flex" gap={1}>
                                                                <Typography variant="body2" sx={{ color: '#2e2e2e' }}>
                                                                    {item.label}:
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: '#000' }}>
                                                                    {systemVersions[item.key] || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            {/* Upgrade Section */}
                            <Grid item xs={12}>
                                <Paper sx={{ 
                                    p: 2, 
                                    border: '1px solid #505050', 
                                    borderRadius: 1, 
                                    bgcolor: '#ffffff'
                                }}>
                                    <Typography variant="h6" gutterBottom>System Upgrade</Typography>
                                     <Box display="flex" gap={2} flexWrap="wrap">
                                        <input
                                            type="file"
                                            hidden
                                            ref={upgradeFileInputRef}
                                            onChange={(e) => {
                                                if (e.target.files[0]) {
                                                    handleUpgradeFile(e.target.files[0], upgradeType);
                                                }
                                            }}
                                        />
                                        <Button 
                                            variant="contained" 
                                            onClick={() => {
                                                setUpgradeType('app');
                                                upgradeFileInputRef.current.click();
                                            }}
                                            disabled={isUpgrading}
                                        >
                                            {isUpgrading && upgradeType === 'app' ? (
                                                <CircularProgress size={24} color="inherit" />
                                            ) : (
                                                'Upgrade App'
                                            )}
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            onClick={() => {
                                                setUpgradeType('bios');
                                                upgradeFileInputRef.current.click();
                                            }}
                                            disabled={isUpgrading}
                                        >
                                            {isUpgrading && upgradeType === 'bios' ? (
                                                <CircularProgress size={24} color="inherit" />
                                            ) : (
                                                'Upgrade BIOS'
                                            )}
                                        </Button>
                                        <Button variant="contained" onClick={() => console.log('Upgrade Boot')} disabled>
                                            Upgrade Boot
                                        </Button>
                                        <Button variant="contained" onClick={() => console.log('Upgrade Net')}>
                                            Upgrade Net
                                        </Button>
                                        <Button variant="contained" onClick={() => console.log('Upgrade FPGA')} disabled>
                                            Upgrade FPGA
                                        </Button>
                                        <Button variant="contained" onClick={() => console.log('Upgrade BAS')} disabled>
                                            Upgrade BAS
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* User Disk Section */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 2, 
                                    border: '1px solid #505050', 
                                    borderRadius: 1, 
                                    bgcolor: '#ffffff',
                                    height: '100%'
                                }}>
                                    <Typography variant="h6" gutterBottom sx={{ marginTop: 0 }}>User Disk: Program data files</Typography>
                                    <Box sx={{ height: 300, overflow: 'auto', mb: 2 }}>
                                        <TableContainer>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ color: '#000', bgcolor: '#ffffff' }}>Name</TableCell>
                                                        <TableCell sx={{ color: '#000', bgcolor: '#ffffff' }}>Type</TableCell>
                                                        <TableCell align="right" sx={{ color: '#000', bgcolor: '#ffffff' }}>Size</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {userDiskFiles ? userDiskFiles.map((file) => (
                                                        <TableRow 
                                                            hover 
                                                            key={file.fileName}
                                                            onClick={() => {
                                                                setSelectedUserFile(file);
                                                            }}
                                                            sx={{ 
                                                                cursor: 'pointer',
                                                                backgroundColor: selectedUserFile?.fileName === file.fileName ? '#e0e0e0' : 'inherit'
                                                            }}
                                                        >
                                                            <TableCell sx={{ color: '#000' }}>{file.fileName}</TableCell>
                                                            <TableCell sx={{ color: '#000' }}>{file.fileExtension}</TableCell>
                                                            <TableCell align="right" sx={{ color: '#000' }}>{formatBytes(file.fileSize)}</TableCell>
                                                        </TableRow>
                                                    )) : <></>}

                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Button 
                                            variant="outlined" 
                                            size="small" 
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch(`${API_BASE_URL}/admin/space-remaining/${deviceId}`, {
                                                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                    });
                                                    const data = await response.json();
                                                    setFreeSpace(data.spaceRemainingMb || '--');
                                                } catch (error) {
                                                    console.error('Error fetching remaining disk space:', error);
                                                    alert('Failed to get remaining disk space.');
                                                }
                                            }}
                                        >
                                            Free space: {freeSpace} MB
                                        </Button>
                                        <Box display="flex" gap={1}>
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                onClick={async () => {
                                                    setUserDiskLoading(true);
                                                    setUserDiskFiles(null);
                                                    try {
                                                        const response = await fetch(`${API_BASE_URL}/admin/device-user-disk/${deviceId}`, {
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        const data = await response.json();
                                                        setUserDiskFiles(data);
                                                    } catch (error) {
                                                        console.error('Error fetching user disk files:', error);
                                                    } finally {
                                                        setUserDiskLoading(false);
                                                    }
                                                }}
                                                disabled={isUserDiskLoading}
                                            >
                                                {isUserDiskLoading ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Refresh'
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                onClick={() => userFileInputRef.current.click()}
                                                disabled={isUploading}
                                            >
                                                {isUploading ? <CircularProgress size={24} color="inherit" /> : 'Write File'}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                onClick={() => handleDownloadUserFile(selectedUserFile, 'user')}
                                                disabled={!selectedUserFile || isDownloading}
                                            >
                                                {isDownloading ? 'Downloading...' : 'Download File'}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                onClick={() => handleForceUserLog(selectedUserFile)}
                                                disabled={!selectedUserFile || isDeleting || isDownloading}
                                                sx={{ ml: 1 }}
                                            >
                                                {isDownloading ? <CircularProgress size={24} /> : 'Force Log'}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                color="error" 
                                                size="small" 
                                                disabled={!selectedUserFile || isDeleting}
                                                onClick={() => {
                                                    setDiskToDelete('01');
                                                    setDeleteConfirmOpen(true);
                                                }}
                                            >
                                                {isDeleting ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Delete File'
                                                )}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* System Disk Section */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 2, 
                                    border: '1px solid #e0e0e0', 
                                    borderRadius: 1, 
                                    bgcolor: '#fff',
                                    height: '100%'
                                }}>
                                    <Typography variant="h6" gutterBottom>System Disk: Font files and configuration files</Typography>
                                    <Box sx={{ height: 300, overflow: 'auto', mb: 2 }}>
                                        <TableContainer>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ color: '#000', bgcolor: '#f5f5f5' }}>Name</TableCell>
                                                        <TableCell sx={{ color: '#000', bgcolor: '#f5f5f5' }}>Type</TableCell>
                                                        <TableCell align="right" sx={{ color: '#000', bgcolor: '#f5f5f5' }}>Size</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {systemDiskFiles ? systemDiskFiles.map((file) => (
                                                        <TableRow 
                                                            hover 
                                                            key={file.fileName}
                                                                onClick={() => {
                                                                    setSelectedSystemFile(file);
                                                                }}
                                                            sx={{ 
                                                                cursor: 'pointer',
                                                                backgroundColor: selectedSystemFile?.fileName === file.fileName ? '#e0e0e0' : 'inherit'
                                                            }}
                                                        >
                                                            <TableCell sx={{ color: '#000' }}>{file.fileName}</TableCell>
                                                            <TableCell sx={{ color: '#000' }}>{file.fileExtension}</TableCell>
                                                            <TableCell align="right" sx={{ color: '#000' }}>{formatBytes(file.fileSize)}</TableCell>
                                                        </TableRow>
                                                    )) : <></>}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Button 
                                            variant="outlined" 
                                            size="small" 
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch(`${API_BASE_URL}/admin/space-remaining/${deviceId}`, {
                                                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                    });
                                                    const data = await response.json();
                                                    setFreeSpace(data.spaceRemainingMb || '--');
                                                } catch (error) {
                                                    console.error('Error fetching remaining disk space:', error);
                                                    alert('Failed to get remaining disk space.');
                                                }
                                            }}
                                        >
                                            Free space: {freeSpace} MB
                                        </Button>
                                        <Box display="flex" gap={1}>
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                onClick={async () => {
                                                    setSystemDiskLoading(true);
                                                    setSystemDiskFiles(null);
                                                    try {
                                                        const response = await fetch(`${API_BASE_URL}/admin/device-system-disk/${deviceId}`, {
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        const data = await response.json();
                                                        setSystemDiskFiles(data);
                                                    } catch (error) {
                                                        console.error('Error fetching system disk files:', error);
                                                    } finally {
                                                        setSystemDiskLoading(false);
                                                    }
                                                }}
                                                disabled={isSystemDiskLoading}
                                            >
                                                {isSystemDiskLoading ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Refresh'
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                onClick={() => fileInputRef.current.click()}
                                                disabled={isUploading}
                                            >
                                                {isUploading ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Write File'
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                disabled={!selectedSystemFile}
                                                onClick={async () => {
                                                    try {
                                                         setIsDownloading(true);
                                                        if (!selectedSystemFile) return;
                                                        const fileName = `${selectedSystemFile.fileName}.${selectedSystemFile.fileExtension}`;
                                                        const disk = '00';
                                                        const response = await fetch(`${API_BASE_URL}/admin/download-file/${deviceId}/${encodeURIComponent(fileName)}/${disk}`, {
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                        });
                                                        if (!response.ok) {
                                                            const errorData = await response.json();
                                                            alert(`Failed to download file: ${errorData.message || 'Unknown error'}`);
                                                        }

                                                        if (response.status === 401) {
                                                            navigate('/unauthorized');
                                                            return;
                                                        }
                                                        // Get filename from Content-Disposition header or use original name
                                                        const contentDisposition = response.headers.get('Content-Disposition');
                                                        const filename = contentDisposition 
                                                            ? contentDisposition.split('filename=')[1].replace(/"/g, '')
                                                            : fileName;

                                                        // Get correct MIME type from response
                                                        const contentType = response.headers.get('Content-Type') || 'application/octet-stream';

                                                        // Handle binary data properly
                                                        const blob = await response.blob();

                                                        const url = URL.createObjectURL(blob);

                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = filename;  // Use sanitized filename
                                                        a.style.display = 'none';
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        document.body.removeChild(a);
                                                        URL.revokeObjectURL(url);
                                                    } catch (error) {
                                                        console.error('Error downloading file:', error);
                                                        alert('Error downloading file. Please try again later.');
                                                    } finally {
                                                        setIsDownloading(false);
                                                    }
                                                }}
                                            >
                                                {isDownloading ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Download File'
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                color="error" 
                                                size="small" 
                                                disabled={!selectedSystemFile || isDeleting}
                                                onClick={() => {
                                                    setDiskToDelete('00');
                                                    setDeleteConfirmOpen(true);
                                                }}
                                            >
                                                {isDeleting ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Delete File'
                                                )}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                    {selectedTab === 'log' && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {/* Left Column */}
                            <Grid item xs={6}>
                                <Grid container spacing={2}>
                                    {/* Stats Row */}
                                    <Grid item xs={12}>
                                        <Grid container spacing={2} sx={{ mb: 4 }}>
                                            <Grid item xs={6}>
                                                <Paper sx={{ p: 2, bgcolor: '#fff', border: '1px solid #e0e0e0' }}>
                                                <Typography variant="h6" gutterBottom sx={{ color: '#000' }}>Total Vehicles</Typography>
                                                {trafficStats ? (
                                                    <Typography variant="h4" sx={{ color: '#0a610e' }}>
                                                    {trafficStats.total.toLocaleString()}
                                                    </Typography>
                                                ) : (
                                                    <CircularProgress size={24} />
                                                )}
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Paper sx={{ p: 2, bgcolor: '#fff', border: '1px solid #e0e0e0' }}>
                                                <Typography variant="h6" gutterBottom sx={{ color: '#000' }}>Speeding Vehicles</Typography>
                                                {trafficStats ? (
                                                    <Typography variant="h4" sx={{ color: '#d90429' }}>
                                                    {trafficStats.speeding.toLocaleString()}
                                                    </Typography>
                                                ) : (
                                                    <CircularProgress size={24} />
                                                )}
                                                </Paper>
                                            </Grid>
                                            </Grid>
                                    </Grid>

                                    {/* Chart Row */}
                                    <Grid item xs={12}>
                                            <Paper sx={{ 
                                                p: 2, 
                                                height: 400, 
                                                bgcolor: '#fff', 
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1
                                            }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                    <Typography variant="h6" gutterBottom sx={{color:'white'}}>Traffic History</Typography>
                                                    <TextField
                                                        select
                                                        value={timeRange}
                                                        onChange={(e) => setTimeRange(e.target.value)}
                                                        size="small"
                                                        sx={{
                                                            minWidth: 120,
                                                            color: 'inherit',
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(76, 175, 80, 0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(76, 175, 80, 0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#4CAF50',
                                                            },
                                                            '.MuiSelect-icon': {
                                                                color: 'inherit'
                                                            }
                                                        }}
                                                        >
                                                        <MenuItem value="daily">Yesterday</MenuItem>
                                                        <MenuItem value="weekly">Last 7 Days</MenuItem>
                                                        <MenuItem value="monthly">Last 30 Days</MenuItem>
                                                    </TextField>
                                                </Box>
                                                
                                                {logChartLoading ? (
                                                    <Box sx={{ 
                                                        height: '90%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <CircularProgress size={40} />
                                                    </Box>
                                                ) : (
                                                    <ResponsiveContainer width="100%" height="90%">
                                                <LineChart data={trafficChartData}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                                                    <XAxis
                                                        dataKey="time"
                                                        tick={{ fill: '#000' }}
                                                    />
                                                    <YAxis tick={{ fill: '#000' }} />
                                                    <Tooltip
                                                        contentStyle={{ 
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #e0e0e0'
                                                        }}
                                                        formatter={(value) => [value.toLocaleString(), 'Count']}
                                                    />
                                                    <Legend />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="total"
                                                        stroke="#82ca9d"
                                                        strokeWidth={2}
                                                        name="Total Vehicles"
                                                    />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="speeding"
                                                        stroke="#ff6b6b"
                                                        strokeWidth={2}
                                                        name="Speeding Vehicles"
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                            )}
                                        </Paper>                                   
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Right Column - Log List */}
                            <Grid item xs={6}>
                                <Paper sx={{ 
                                    p: 2, 
                                    height: 'calc(100vh - 300px)', 
                                    bgcolor: '#fff', 
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1
                                }}>
                                    <Typography variant="h6" gutterBottom sx={{color:'#000'}}>Recent Logs</Typography>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12}>
                                            <DateRangeFilter />
                                        </Grid>

                                    </Grid>
                                    
                                    <Box sx={{ height: '80%', overflow: 'auto' }}>
                                        {logTableLoading ? (
                                            <Box sx={{ 
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <CircularProgress />
                                            </Box>
                                        ) : (
                                            logEntries?.map((log, index) => (
                                                <Paper key={index} sx={{ 
                                                    p: 1.5, 
                                                    mb: 0.5,
                                                    bgcolor: '#f5f5f5',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 1
                                                }}>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={4}>
                                                            <Typography variant="body2" sx={{ color: '#000' }}>
                                                                {`${log.year}-${log.month.toString().padStart(2, '0')}-${log.day.toString().padStart(2, '0')}`}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="body2" sx={{ color: '#000' }}>
                                                                {log.detected_at}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={2}>
                                                            <Typography variant="body2" sx={{ color: '#444444' }}>
                                                                {log.speed}km/h
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={2}>
                                                            <Typography variant="body2" sx={{ color: '#555555' }}>
                                                                {log.direction}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Paper>
                                            ))
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                    {selectedTab === 'service' && (
                        <Box sx={{ mt: 2 }}>
                        {!tabData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                            <CircularProgress />
                            <Typography variant="body1" sx={{ ml: 2 }}>
                                Loading service notes...
                            </Typography>
                            </Box>
                        ) : !Array.isArray(tabData) || tabData.length === 0 ? (
                            <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No service logs found for this device.
                            </Typography>
                            </Paper>
                        ) : (
                            <Grid container spacing={2}>
                            {tabData?.map((log) => (
                                <Grid item xs={12} key={log.id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderColor:
                                        log.severity === 'green' ? 'success.main' :
                                        log.severity === 'yellow' ? 'warning.main' :
                                        log.severity === 'red' ? 'error.main' : 'grey.400',
                                    borderWidth: 2,
                                    borderStyle: 'solid',
                                    }}
                                >
                                    <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="heading1" color="text.primary">
                                            {new Date(log.created_at).toLocaleDateString()}
                                        </Typography>
                                        <Chip
                                            label={getSeverityLabel(log.severity)}
                                            size="small"
                                            sx={{
                                            backgroundColor:
                                                log.severity === 'green' ? 'success.light' :
                                                log.severity === 'yellow' ? 'warning.light' :
                                                log.severity === 'red' ? 'error.light' : 'grey.300',
                                            color:
                                                log.severity === 'grey' ? 'grey.800' : 'white'
                                            }}
                                        />
                                        </Box>
                                        <Box>
                                        {editingLogId === log.id ? (
                                            <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleSaveClick(log.id)}
                                            sx={{ mr: 1 }}
                                            >
                                            Save
                                            </Button>
                                        ) : (
                                            <IconButton size="small" onClick={() => handleEditClick(log)} sx={{ mr: 1 }}>
                                            <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton size="small" onClick={() => handleDeleteClick(log.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                        </Box>
                                    </Box>
                                    <FormControl fullWidth margin="dense" sx={{ mb: 1 }}>
                                    <InputLabel>Service Type</InputLabel>
                                    <Select
                                        label="Service Type"
                                        value={editingLogId === log.id ? editedLogData.serviceType : log.serviceType}
                                        onChange={(e) => handleFieldChange(e, 'serviceType')}
                                        disabled={editingLogId !== log.id}
                                        variant="outlined"
                                    >
                                        <MenuItem value="inspection">Inspection</MenuItem>
                                        <MenuItem value="maintenance">Routine Maintenance</MenuItem>
                                        <MenuItem value="upgrade">Software/Hardware Upgrade</MenuItem>
                                        <MenuItem value="repair">Repair</MenuItem>
                                        <MenuItem value="calibration">Calibration</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                                    <TextField
                                        label="Performed By"
                                        fullWidth
                                        margin="dense"
                                        value={editingLogId === log.id ? editedLogData.performedBy : log.performedBy}
                                        onChange={(e) => handleFieldChange(e, 'performedBy')}
                                        disabled={editingLogId !== log.id}
                                        variant="outlined"
                                        sx={{ mb: 1 }}
                                    />
                                    <TextField
                                        label="Description"
                                        fullWidth
                                        margin="dense"
                                        value={editingLogId === log.id ? editedLogData.description : log.description}
                                        onChange={(e) => handleFieldChange(e, 'description')}
                                        disabled={editingLogId !== log.id}
                                        variant="outlined"
                                        sx={{ 
                                            mb: 1,
                                            '& .MuiInputBase-root': {
                                                resize: 'vertical',
                                                minHeight: '120px',
                                                overflow: 'auto',
                                            }
                                        }}
                                        multiline
                                        rows={4}
                                    />
                                    <TextField
                                        label="Details"
                                        fullWidth
                                        margin="dense"
                                        multiline
                                        rows={4}
                                        value={editingLogId === log.id ? editedLogData.details : log.details}
                                        onChange={(e) => handleFieldChange(e, 'details')}
                                        disabled={editingLogId !== log.id}
                                        variant="outlined"
                                        sx={{ 
                                            mb: 1,
                                            '& .MuiInputBase-root': {
                                                resize: 'vertical',
                                                minHeight: '120px',
                                                overflow: 'auto',
                                            }
                                        }}
                                    />
                                    <TextField
                                        label="Internal Notes"
                                        fullWidth
                                        margin="dense"
                                        multiline
                                        rows={4}
                                        value={editingLogId === log.id ? editedLogData.notes : log.notes}
                                        onChange={(e) => handleFieldChange(e, 'notes')}
                                        disabled={editingLogId !== log.id}
                                        variant="outlined"
                                        sx={{ 
                                            mb: 2,
                                            '& .MuiInputBase-root': {
                                                resize: 'vertical',
                                                minHeight: '120px',
                                                overflow: 'auto',
                                            }
                                        }}
                                    />
                                    {/* Image Display and Upload Section */}
                                    <Typography variant="subtitle2" gutterBottom>
                                        Images:
                                    </Typography>
                                    <Grid container spacing={1} sx={{ mb: 2 }}>
                                        {(editingLogId === log.id ? editedLogData.images : log.images)?.map((image) => (
                                        <Grid item key={image.id}>
                                            <Box 
                                                sx={{ 
                                                    position: 'relative', 
                                                    width: 200, 
                                                    height: 200, 
                                                    border: '1px solid #ccc', 
                                                    borderRadius: 1, 
                                                    overflow: 'hidden',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => {
                                                    setEnlargedImage(image.data);
                                                    setImageDialogOpen(true);
                                                }}
                                            >
                                                <img
                                                    src={image.data}
                                                    alt={image.name}
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                />
                                                {editingLogId === log.id && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            right: 0,
                                                            backgroundColor: 'rgba(255,255,255,0.7)',
                                                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // 防止触发图像放大
                                                            handleRemoveImage(image.id);
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Grid>
                                        ))}
                                        {editingLogId === log.id && (
                                        <Grid item>
                                            <input
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            id={`upload-button-${log.id}`}
                                            multiple
                                            type="file"
                                            onChange={handleImageUpload}
                                            />
                                            <label htmlFor={`upload-button-${log.id}`}>
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                startIcon={<AddPhotoAlternateIcon />}
                                                sx={{ width: 100, height: 100, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
                                            >
                                                Upload
                                            </Button>
                                            </label>
                                        </Grid>
                                        )}
                                    </Grid>
                                    </CardContent>
                                </Card>
                                </Grid>
                            ))}
                            </Grid>
                        )}
                        </Box>
                    )}

                    {/* Delete Confirmation Dialog */}
                    <Dialog
                        open={openDeleteDialog}
                        onClose={handleCloseDeleteDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
                        <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to delete this service note? This action cannot be undone.
                        </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                        <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                        <Button onClick={handleConfirmDelete} autoFocus color="error">
                            Delete
                        </Button>
                        </DialogActions>
                    </Dialog>
                    
                        
                </>}
            </Box>
            {deleteError && (
                <Snackbar 
                    open={!!deleteError} 
                    autoHideDuration={6000} 
                    onClose={() => setDeleteError(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert 
                        severity="error" 
                        sx={{ width: '100%' }} 
                        onClose={() => setDeleteError(null)}
                    >
                        {deleteError}
                    </Alert>
                </Snackbar>
            )}
            {deleteSuccess && (
                <Snackbar 
                    open={!!deleteSuccess} 
                    autoHideDuration={6000} 
                    onClose={() => setDeleteSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert 
                        severity="success" 
                        sx={{ width: '100%' }} 
                        onClose={() => setDeleteSuccess(false)}
                    >
                        File deleted successfully
                    </Alert>
                </Snackbar>
            )}
            {deleteConfirmOpen && (
                <Dialog 
                    open={deleteConfirmOpen} 
                    onClose={() => setDeleteConfirmOpen(false)}
                    PaperProps={{
                        sx: {
                            bgcolor: '#1a1a1a',
                            color: 'white',
                            '& .MuiDialogTitle-root': {
                                borderBottom: '1px solid #333',
                                padding: '16px 24px'
                            }
                        }
                    }}
                >
                    <DialogTitle>
                        Type '{(diskToDelete === '00' ? selectedSystemFile : selectedUserFile)?.fileName}.{(diskToDelete === '00' ? selectedSystemFile : selectedUserFile)?.fileExtension}' to confirm delete
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Filename"
                            fullWidth
                            value={fileNameToDelete}
                            onChange={(e) => setFileNameToDelete(e.target.value)}
                            sx={{
                                '& .MuiInputLabel-root': { color: '#b0b0b0' },
                                '& .MuiInputBase-input': { color: 'white' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#404040' },
                                    '&:hover fieldset': { borderColor: '#606060' },
                                    '&.Mui-focused fieldset': { borderColor: '#4CAF50' }
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid #333' }}>
                        <Button 
                            onClick={() => setDeleteConfirmOpen(false)}
                            sx={{ color: '#b0b0b0' }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            color="error"
                            onClick={() => handleDeleteFile(
                                diskToDelete === '00' ? selectedSystemFile : selectedUserFile,
                                diskToDelete,
                                diskToDelete === '00' ? setSystemDiskFiles : setUserDiskFiles
                            )}
                            disabled={isDeleting}
                            sx={{ '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}
                        >
                            {isDeleting ? <CircularProgress size={24} /> : 'Confirm Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {writeError && (
                <Snackbar 
                    open={!!writeError} 
                    autoHideDuration={6000} 
                    onClose={() => setWriteError(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert severity="error" onClose={() => setWriteError(null)}>
                        {writeError}
                    </Alert>
                </Snackbar>
            )}

            {writeSuccess && (
                <Snackbar 
                    open={!!writeSuccess} 
                    autoHideDuration={6000} 
                    onClose={() => setWriteSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert severity="success" onClose={() => setWriteSuccess(false)}>
                        File uploaded successfully
                    </Alert>
                </Snackbar>
            )}
            {forceLogError && (
                <Snackbar 
                    open={!!forceLogError} 
                    autoHideDuration={6000} 
                    onClose={() => setForceLogError(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert severity="error" sx={{ width: '100%' }}>
                        Force log failed: {forceLogError}
                    </Alert>
                </Snackbar>
            )}

            {forceLogSuccess && (
                <Snackbar 
                    open={forceLogSuccess}
                    autoHideDuration={6000}
                    onClose={() => setForceLogSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert severity="success" sx={{ width: '100%' }}>
                        Log forced successfully - log updated to server
                    </Alert>
                </Snackbar>
            )}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    setIsUploading(true);
                    try {
                        const formData = new FormData();
                        formData.append('file', file);

                        const response = await fetch(`${API_BASE_URL}/admin/write-file/${deviceId}/00`, {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            body: formData
                        });

                        if (!response.ok) {
                            const errorData = await response.json();
                            throw new Error(errorData.message || 'File upload failed');
                        }

                        if (response.status === 401) {
                            navigate('/unauthorized');
                            return;
                        }

                        // Refresh file list after successful upload
                        const refreshRes = await fetch(`${API_BASE_URL}/admin/device-system-disk/${deviceId}`, {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                        });
                        const data = await refreshRes.json();
                        setSystemDiskFiles(data);
                        
                        setWriteSuccess(true);
                        setWriteError(null);
                    } catch (error) {
                        setWriteError(error.message);
                        setWriteSuccess(false);
                    } finally {
                        setIsUploading(false);
                        // setSelectedFile(null);
                        fileInputRef.current.value = '';
                    }
                }}
            />
            <input
                type="file"
                ref={userFileInputRef}
                style={{ display: 'none' }}
                onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    setIsUploading(true);
                    try {
                        const formData = new FormData();
                        formData.append('file', file);

                        const response = await fetch(`${API_BASE_URL}/admin/write-file/${deviceId}/01`, {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            body: formData
                        });

                        if (!response.ok) {
                            const errorData = await response.json();
                            throw new Error(errorData.message || 'File upload failed');
                        }

                        if (response.status === 401) {
                            navigate('/unauthorized');
                            return;
                        }

                        // Refresh user disk files after upload
                        const refreshRes = await fetch(`${API_BASE_URL}/admin/device-user-disk/${deviceId}`, {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                        });
                        const data = await refreshRes.json();
                        setUserDiskFiles(data);
                        
                        setWriteSuccess(true);
                        setWriteError(null);
                    } catch (error) {
                        setWriteError(error.message);
                        setWriteSuccess(false);
                    } finally {
                        setIsUploading(false);
                        userFileInputRef.current.value = '';
                    }
                }}
            />
            <Dialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'visible',
                        maxWidth: 'none',
                        maxHeight: 'none',
                        margin: 0
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        backdropFilter: 'blur(3px)'
                    }
                }}
            >
                <Box 
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        width: '100vw',
                        overflow: 'hidden'
                    }}
                >
                    {enlargedImage && (
                        <Box
                            sx={{
                                position: 'relative',
                                transformOrigin: 'center center',
                                transition: 'transform 0.1s ease',
                                cursor: 'grab',
                                '&:active': {
                                    cursor: 'grabbing'
                                }
                            }}
                            onWheel={(e) => {
                                e.preventDefault();
                                const scaleDelta = e.deltaY > 0 ? 0.9 : 1.1;
                                setImageScale(prev => Math.max(0.1, Math.min(5, prev * scaleDelta)));
                            }}
                            onMouseDown={(e) => {
                                if (e.button === 0) { // Left click only
                                    setIsDragging(true);
                                    setDragStart({ x: e.clientX, y: e.clientY });
                                    setImagePosition({ x: 0, y: 0 });
                                }
                            }}
                            onMouseMove={(e) => {
                                if (isDragging) {
                                    const deltaX = e.clientX - dragStart.x;
                                    const deltaY = e.clientY - dragStart.y;
                                    setImagePosition({ x: deltaX, y: deltaY });
                                }
                            }}
                            onMouseUp={() => setIsDragging(false)}
                            onMouseLeave={() => setIsDragging(false)}
                        >
                            <img
                                src={enlargedImage}
                                alt="Enlarged view"
                                style={{ 
                                    maxWidth: '90vw',
                                    maxHeight: '90vh',
                                    objectFit: 'contain',
                                    display: 'block',
                                    transform: `scale(${imageScale}) translate(${imagePosition.x}px, ${imagePosition.y}px) rotate(${imageRotation}deg)`,
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                    transition: 'transform 0.1s ease'
                                }}
                                onClick={(e) => {
                                    if (!isDragging) {
                                        setImageDialogOpen(false);
                                    }
                                }}
                            />
                        </Box>
                    )}
                    <Box 
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            display: 'flex',
                            gap: 1,
                            zIndex: 1
                        }}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setImageScale(prev => Math.min(5, prev * 1.2))}
                            sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                        >
                            +
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setImageScale(prev => Math.max(0.1, prev * 0.8))}
                            sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                        >
                            -
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                                setImageScale(1);
                                setImagePosition({ x: 0, y: 0 });
                                setImageRotation(0);
                            }}
                            sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setImageRotation(prev => (prev + 90) % 360)}
                            sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                        >
                            ↻
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setImageRotation(prev => (prev - 90 + 360) % 360)}
                            sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                        >
                            ↺
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setImageDialogOpen(false)}
                            sx={{ backgroundColor: 'rgba(255, 0, 0, 0.5)', color: 'white' }}
                        >
                            X
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
    
};

export default DeviceDetails;



